// BSG: Pick Your Poison carousel.
//
// Two independent jobs in one file, because they share a section:
//   1. The delegated size-pill selector — swaps the selected variant in place,
//      retitles the CTA, and tells the sticky buy bar what was last tapped.
//   2. The carousel controller — tabs, paging, progress bars, editor lifecycle.
//
// Loaded by sections/candells-product-grid.liquid with defer, so the document is
// already parsed when this runs.
(function () {
  'use strict';

  var GAP_FALLBACK = 14;
  var BREAKPOINTS = ['(min-width: 620px)', '(min-width: 900px)', '(min-width: 1180px)'];

  /* ── Size pills ─────────────────────────────────────────────── */

  function onPillClick(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var pill = target.closest('.candells-card__size-pill');
    if (!pill || pill.disabled) return;

    var card = pill.closest('[data-candells-product-card]');
    if (!card) return;

    card.querySelectorAll('.candells-card__size-pill').forEach(function (p) {
      p.classList.remove('is-selected');
      p.setAttribute('aria-pressed', 'false');
    });
    pill.classList.add('is-selected');
    pill.setAttribute('aria-pressed', 'true');

    var input = card.querySelector('.product-variant-id');
    if (input) input.value = pill.dataset.variantId;

    // Both variant photos are already in the DOM, stacked, and CSS picks the
    // visible one from this attribute. Previously this rewrote the single
    // image's src, which meant every pill tap paid a cold network round trip
    // for a photo that had never been requested — the swap could take
    // seconds on mobile. Setting one attribute costs nothing.
    var media = card.querySelector('.candells-card__media');
    if (media && pill.dataset.sizeLabel) {
      media.setAttribute('data-candells-selected-size', pill.dataset.sizeLabel);
    }

    var submitButton = card.querySelector('.candells-card__submit');

    // The CTA tracks the chosen size ("Add to cart · $16"). Skipped while the
    // button is disabled so a sold-out card keeps saying "Sold out".
    var cta = card.querySelector('[data-candells-cta]');
    if (cta && cta.dataset.candellsCtaTemplate && submitButton && !submitButton.disabled) {
      cta.textContent = cta.dataset.candellsCtaTemplate.replace(
        '[price]',
        pill.dataset.priceLabel || pill.dataset.price || ''
      );
    }

    document.dispatchEvent(
      new CustomEvent('candells:variant:picked', {
        detail: {
          variantId: pill.dataset.variantId,
          title: submitButton ? submitButton.dataset.candellsProductTitle : '',
          url: submitButton ? submitButton.dataset.candellsProductUrl : '',
          image: submitButton ? submitButton.dataset.candellsProductImage : '',
          size: pill.dataset.sizeLabel,
          price: pill.dataset.price,
        },
      })
    );
  }

  /* ── Carousel ───────────────────────────────────────────────── */

  function setUp(root) {
    var track = root.querySelector('[data-candells-carousel-track]');
    if (!track) return;

    var cards = Array.prototype.slice.call(track.querySelectorAll('[data-candells-product-card]'));
    var trailing = track.querySelector('[data-candells-carousel-trailing]');
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-candells-carousel-tab]'));
    var prev = root.querySelector('[data-candells-carousel-prev]');
    var next = root.querySelector('[data-candells-carousel-next]');
    var dots = root.querySelector('[data-candells-carousel-dots]');
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

    var page = 0;
    var items = cards.length + (trailing ? 1 : 0);

    // --per-view is set by the media queries in candells-product-grid.css and
    // read back here, so the CSS and the JS can never disagree about how many
    // cards make a page.
    function perView() {
      var value = parseInt(window.getComputedStyle(track).getPropertyValue('--per-view'), 10);
      return value > 0 ? value : 1;
    }

    function gap() {
      var value = parseFloat(window.getComputedStyle(track).columnGap);
      return value > 0 ? value : GAP_FALLBACK;
    }

    // One card width plus one gap. Measured off the first card that is actually
    // showing, so a filtered track steps by the right amount.
    function step() {
      var first = track.querySelector(
        '[data-candells-product-card]:not([hidden]), [data-candells-carousel-trailing]'
      );
      var width = first ? first.getBoundingClientRect().width + gap() : track.clientWidth;
      return width > 0 ? width : 1;
    }

    // Derived from data, never from scrollWidth/clientWidth: on a tab switch the
    // DOM still holds the previous filter's measurements when this is called.
    // The trailing card counts as an item only when it is actually rendered.
    function pages() {
      return Math.max(1, items - perView() + 1);
    }

    function paint() {
      var total = pages();
      var idle = total < 2;
      var i;

      if (prev) {
        prev.disabled = idle;
        prev.classList.toggle('is-idle', idle);
      }
      if (next) {
        next.disabled = idle;
        next.classList.toggle('is-idle', idle);
      }
      if (!dots) return;

      // Emptied rather than hidden: base.css `div:empty { display: none }` is
      // what takes the row out of the layout.
      if (idle) {
        if (dots.children.length) dots.innerHTML = '';
        return;
      }

      if (dots.children.length !== total) {
        var label = dots.getAttribute('data-label') || '';
        dots.innerHTML = '';
        for (i = 0; i < total; i++) {
          var bar = document.createElement('button');
          bar.type = 'button';
          bar.className = 'candells-carousel__dot';
          bar.setAttribute('data-page', String(i));
          bar.setAttribute('aria-label', label.replace('[n]', i + 1).replace('[total]', total));
          dots.appendChild(bar);
        }
      }

      for (i = 0; i < dots.children.length; i++) {
        if (i === page) dots.children[i].setAttribute('aria-current', 'true');
        else dots.children[i].removeAttribute('aria-current');
      }
    }

    // Page-indexed, never scrollBy: rapid clicks during an in-flight smooth
    // scroll would otherwise compound from a mid-scroll scrollLeft.
    function goTo(index) {
      page = Math.max(0, Math.min(index, pages() - 1));
      track.scrollTo({ left: page * step(), behavior: reduce.matches ? 'auto' : 'smooth' });
      paint();
    }

    function filter(category) {
      var shown = 0;
      var i;
      for (i = 0; i < cards.length; i++) {
        var list = ' ' + (cards[i].getAttribute('data-categories') || '') + ' ';
        var show = category === 'all' || list.indexOf(' ' + category + ' ') > -1;
        cards[i].hidden = !show;
        if (show) shown++;
      }
      items = shown + (trailing ? 1 : 0);
      // Assigning scrollLeft also cancels any smooth scroll still running.
      track.scrollLeft = 0;
      page = 0;
      paint();
    }

    function onClick(event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var tab = target.closest('[data-candells-carousel-tab]');
      if (tab) {
        for (var i = 0; i < tabs.length; i++) {
          tabs[i].setAttribute('aria-selected', tabs[i] === tab ? 'true' : 'false');
        }
        filter(tab.getAttribute('data-category') || 'all');
        return;
      }

      var dot = target.closest('.candells-carousel__dot');
      if (dot) {
        goTo(parseInt(dot.getAttribute('data-page'), 10) || 0);
        return;
      }

      if (target.closest('[data-candells-carousel-prev]')) {
        goTo(page - 1);
        return;
      }
      if (target.closest('[data-candells-carousel-next]')) {
        goTo(page + 1);
      }
    }

    function onScroll() {
      var index = Math.round(track.scrollLeft / step());
      index = Math.max(0, Math.min(index, pages() - 1));
      if (index !== page) {
        page = index;
        paint();
      }
    }

    function onKeydown(event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(page + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(page - 1);
      }
    }

    // Crossing a breakpoint changes perView, so the page index has to be
    // re-clamped and scrollLeft re-aligned to the new step.
    function onBreakpoint() {
      goTo(page);
    }

    var queries = BREAKPOINTS.map(function (query) {
      return window.matchMedia(query);
    });
    queries.forEach(function (query) {
      if (query.addEventListener) query.addEventListener('change', onBreakpoint);
      else if (query.addListener) query.addListener(onBreakpoint);
    });

    root.addEventListener('click', onClick);
    track.addEventListener('scroll', onScroll, { passive: true });
    track.addEventListener('keydown', onKeydown);

    root._candellsDetach = function () {
      queries.forEach(function (query) {
        if (query.removeEventListener) query.removeEventListener('change', onBreakpoint);
        else if (query.removeListener) query.removeListener(onBreakpoint);
      });
      root.removeEventListener('click', onClick);
      track.removeEventListener('scroll', onScroll);
      track.removeEventListener('keydown', onKeydown);
    };

    paint();
  }

  function init(scope) {
    if (!scope || !scope.querySelectorAll) return;
    var roots = scope.querySelectorAll('[data-candells-section="product-grid"]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].getAttribute('data-candells-carousel-ready')) continue;
      roots[i].setAttribute('data-candells-carousel-ready', 'true');
      setUp(roots[i]);
    }
  }

  function teardown(scope) {
    if (!scope || !scope.querySelectorAll) return;
    var roots = scope.querySelectorAll('[data-candells-section="product-grid"]');
    for (var i = 0; i < roots.length; i++) {
      if (typeof roots[i]._candellsDetach === 'function') {
        roots[i]._candellsDetach();
        roots[i]._candellsDetach = null;
      }
    }
  }

  // The document-level listeners must be registered exactly once. The theme
  // editor can re-insert this section (and with it this script tag), and a
  // second click listener would dispatch candells:variant:picked twice.
  if (!document.documentElement.hasAttribute('data-candells-product-grid-bound')) {
    document.documentElement.setAttribute('data-candells-product-grid-bound', 'true');
    document.addEventListener('click', onPillClick);
    document.addEventListener('shopify:section:load', function (event) {
      init(event.target);
    });
    document.addEventListener('shopify:section:unload', function (event) {
      teardown(event.target);
    });
  }

  init(document);
})();
